import { cn } from "@/lib/utils";
import { useTheme } from "@/context/ThemeContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";

const CARRIER_SLUGS: Record<string, string> = {
  MSC: "msc",
  MSCU: "msc",
  MEDU: "msc",
  ZIM: "zim",
  ZIMU: "zim",
  ONE: "one",
  ONEY: "one",
  MSK: "msk",
  MAERSK: "msk",
  MAEU: "msk",
  EMC: "emc",
  EGLV: "emc",
  EISU: "emc",
  YML: "yml",
  YMLU: "yml",
  OOCL: "oocl",
  HLC: "hlc",
  HLCU: "hlc",
  "HAPAG-LLOYD": "hlc",
  HPL: "hlc",
};

const DARK_FILL_CARRIERS = new Set(["msc", "zim", "one", "msk"]);

function slugFor(carrier: string): string | null {
  return CARRIER_SLUGS[carrier.trim().toUpperCase()] ?? null;
}

/** Whether we have a bundled SVG logo for this carrier / SCAC code. */
export function carrierHasLogo(carrier: string | null | undefined): boolean {
  if (!carrier?.trim()) return false;
  return slugFor(carrier) != null;
}

/** Build a carrier logo `<img>` for non-React surfaces (e.g. contenteditable pills). */
export function createCarrierLogoImg(
  carrier: string,
  size: number,
  theme: "light" | "dark",
): HTMLImageElement | null {
  const slug = slugFor(carrier);
  if (!slug) return null;
  const img = document.createElement("img");
  img.src = `/carriers/${slug}.svg`;
  img.alt = "";
  img.draggable = false;
  img.className = cn(
    "inline-block shrink-0 object-contain object-left",
    theme === "dark" && DARK_FILL_CARRIERS.has(slug) && "brightness-0 invert",
  );
  img.style.height = `${size}px`;
  img.style.width = "auto";
  img.style.minWidth = `${size}px`;
  return img;
}

export function formatCarrierLabel(carrier: string): string {
  const key = carrier.trim().toUpperCase();
  if (key === "MSK" || key === "MAERSK" || key === "MAEU") return "Maersk";
  if (key.startsWith("MSC")) return "MSC";
  return carrier.trim();
}

interface CarrierLogoProps {
  carrier: string | null | undefined;
  size?: number;
  showLogo?: boolean;
  bold?: boolean;
  className?: string;
  /**
   * Render only the logo image (no carrier label). When the logo can't be
   * shown (unknown carrier or logos disabled) nothing is rendered, so callers
   * that already display the carrier elsewhere don't get a duplicate label.
   */
  logoOnly?: boolean;
}

export function CarrierLogo({
  carrier,
  size = 18,
  showLogo,
  bold = false,
  className,
  logoOnly = false,
}: CarrierLogoProps) {
  const { theme } = useTheme();
  const { preferences } = useUserPreferences();
  const resolvedShowLogo = showLogo ?? preferences.showCarrierLogos;

  if (!carrier) {
    return logoOnly ? null : <span className="text-muted-foreground">—</span>;
  }

  const slug = slugFor(carrier);
  const label = formatCarrierLabel(carrier);

  if (!slug || !resolvedShowLogo) {
    return logoOnly ? null : (
      <span className={cn(bold && "font-semibold", className)}>{label}</span>
    );
  }

  const needsInvert = theme === "dark" && DARK_FILL_CARRIERS.has(slug);
  const img = (
    <img
      src={`/carriers/${slug}.svg`}
      alt={logoOnly ? label : ""}
      className={cn(
        "block shrink-0 object-contain object-left",
        needsInvert && "brightness-0 invert",
      )}
      style={{ height: size, width: "auto", minWidth: size }}
    />
  );

  if (logoOnly) {
    return (
      <span className={cn("inline-flex shrink-0 items-center", className)}>
        {img}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex min-w-0 max-w-full items-center gap-1.5 align-middle",
        className,
      )}
    >
      <span className="inline-flex shrink-0 items-center">{img}</span>
      <span className={cn("min-w-0 truncate", bold && "font-semibold")}>
        {label}
      </span>
    </span>
  );
}
